import * as React from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import AnalyticsOutlinedIcon from "@mui/icons-material/AnalyticsOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { Link as RouterLink } from "react-router-dom";

import LatestBlogPreview from "./LatestBlogPreview";
import RecommenderSection from "./RecommenderSection";
import { SUPPLY_CHAIN_DASHBOARD_URL } from "../config/urls";
import { canUseRecommender } from "../lib/recommenderAccess";

const previewCardSx = {
  height: "100%",
  minHeight: { lg: 620 },
  display: "flex",
  flexDirection: "column",
  p: { xs: 1.5, md: 2.5 },
  border: "1px solid #e5e7eb",
  borderRadius: 3,
  bgcolor: "#fff",
  boxShadow: "0 6px 24px rgba(15, 23, 42, 0.06)",
};

const actionButtonSx = {
  mt: "auto",
  "&&": {
    backgroundColor: "#f7b900",
    backgroundImage: "none",
    color: "#171717",
    borderColor: "#f7b900",
    boxShadow: "none",
  },
  "&&:hover, &&:focus-visible": {
    backgroundColor: "#d99b00",
    backgroundImage: "none",
    color: "#171717",
    borderColor: "#d99b00",
    boxShadow: "none",
  },
  fontWeight: 700,
};

function PreviewHeader({ icon, title, description }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          borderRadius: 2,
          bgcolor: "#fff7d6",
          color: "#d99b00",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography component="h3" sx={{ fontSize: "1.05rem", fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography
          color="text.secondary"
          sx={{ mt: 0.35, fontSize: "0.78rem", lineHeight: 1.5 }}
        >
          {description}
        </Typography>
      </Box>
    </Stack>
  );
}

export default function MainGrid() {
  const canAccessRecommender = canUseRecommender();
  return (
    <Box sx={{ width: "100%", maxWidth: 2000, mx: "auto", mt: 5 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography component="h2" sx={{ fontSize: "1.65rem", fontWeight: 700 }}>
          Explore the SEEDS platform
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.75, fontSize: "0.9rem" }}>
          Research insights, field intelligence, and supply-chain tools in one
          connected workspace.
        </Typography>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="stretch">
        <Grid size={{ xs: 12, md: 6, lg: canAccessRecommender ? 4 : 6 }}>
          <Card
            sx={{
              ...previewCardSx,
              ...(!canAccessRecommender && {
                height: { xs: "auto", md: 650 },
                minHeight: 0,
              }),
            }}
          >
            <PreviewHeader
              icon={<ArticleOutlinedIcon />}
              title="INNOVakis Blog"
              description="Latest research, field results, and practical agricultural insights."
            />

            <Box
              sx={{
                flexGrow: canAccessRecommender ? 1 : 0,
                minWidth: 0,
                mb: 4,
                "& .slick-prev": { left: -8, zIndex: 2 },
                "& .slick-next": { right: -8, zIndex: 2 },
                "& .slick-dots": { bottom: -28 },
              }}
            >
              <LatestBlogPreview
                limit={6}
                showHeader={false}
                cardHeight={canAccessRecommender ? 560 : 440}
              />
            </Box>

            <Button
              component={RouterLink}
              to="/blog"
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              fullWidth
              sx={actionButtonSx}
            >
              Explore the blog
            </Button>
          </Card>
        </Grid>

        {canAccessRecommender ? (
          <Grid size={{ xs: 12, md: 6, lg: 4 }}>
            <Card sx={previewCardSx}>
              <PreviewHeader
                icon={<AnalyticsOutlinedIcon />}
                title="Recommender"
                description="Preview field selection and aggregated environmental data."
              />
  
              <Box sx={{ flexGrow: 1, minWidth: 0, mb: 2 }}>
                <RecommenderSection />
              </Box>
  
              <Button
                component={RouterLink}
                to="/plant"
                variant="contained"
                endIcon={<ArrowForwardRoundedIcon />}
                fullWidth
                sx={actionButtonSx}
              >
                Open Recommender
              </Button>
            </Card>
          </Grid>
        ) : null}

        <Grid
          size={{
            xs: 12,
            md: canAccessRecommender ? 12 : 6,
            lg: canAccessRecommender ? 4 : 6,
          }}
        >
          <Card
            sx={{
              ...previewCardSx,
              ...(!canAccessRecommender && {
                height: { xs: "auto", md: 650 },
                minHeight: 0,
              }),
            }}
          >
            <PreviewHeader
              icon={<LocalShippingOutlinedIcon />}
              title="Supply Chain"
              description="Explore production, trade, consumption, markets, and logistics data."
            />

            <Box
              sx={{
                flexGrow: canAccessRecommender ? 1 : 0,
                height: canAccessRecommender ? "auto" : 460,
                minHeight: canAccessRecommender ? 420 : 0,
                mb: 2,
                overflow: "hidden",
                border: "1px solid #e5e7eb",
                borderRadius: 2.5,
              }}
            >
              <Box
                component="img"
                src="/images/supply-chain.png"
                alt="Supply chain logistics with shipping containers, trucks, and air transport"
                sx={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                  minHeight: canAccessRecommender ? 420 : 0,
                  objectFit: "cover",
                }}
              />
            </Box>

            <Button
              component="a"
              href={SUPPLY_CHAIN_DASHBOARD_URL}
              variant="contained"
              endIcon={<ArrowForwardRoundedIcon />}
              fullWidth
              sx={actionButtonSx}
            >
              View Supply Chain
            </Button>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
